import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import Field from 'containers/Field';
import Button from 'common/Button';
import { CloseXAshIcon } from '@glu/icons-react';
import pageActions from 'core/page/actions';

import buttonLinkStyles from 'styles/buttonLink';

const styles = ({ palette, spacing, typography }) => ({
  edit: {
    ...buttonLinkStyles({ palette, spacing, typography }),
    color: palette.secondary,
    fontSize: 14,
  },
  editingListItem: {},
  icon: {
    '& svg': {
      fill: palette.secondary,
    },
    float: 'right',
    marginLeft: spacing.unit,
    position: 'relative',
    top: 5,
  },
  label: {
    fontSize: 16,
    fontWeight: typography.fontWeights.bold,
    width: '33%',
  },
  listItem: {
    '&$editingListItem': {
      '& $toolbar': {
        top: -15,
      },
      border: 'none',
      marginBottom: spacing.xl * 1.5,
      padding: 0,
    },
    border: `1px solid ${palette.form.border}`,
    borderRadius: 4,
    marginBottom: spacing.sm,
    padding: 12,
    position: 'relative',
  },
  listItemRow: {
    display: 'flex',
    marginBottom: 5,
    width: '100%',
  },
  remove: {
    ...buttonLinkStyles({ palette, spacing, typography }),
    color: palette.secondary,
    fontSize: 14,
    marginLeft: spacing.sm,
  },
  resendButton: {
    ...buttonLinkStyles({ palette, spacing, typography }),
    color: palette.secondary,
    fontSize: 14,
  },
  roles: {
    fontSize: 20,
    marginBottom: spacing.xs,
  },
  title: {
    display: 'inline-block',
    fontSize: 20,
    fontWeight: typography.fontWeights.bold,
    paddingBottom: spacing.unit,
    paddingRight: 125,
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    position: 'absolute',
    right: 12,
    top: 16,
  },
  value: {
    fontSize: 16,
  },
});

const ListGroup = ({
  classes,
  data,
  disableButton,
  fields,
  groupName,
  hideRemove,
  index,
  isEditing,
  isPrimary,
  listGroup,
  onContinue,
  onDone,
  onEdit,
  onRemove,
  onResendEmail,
  primaryResources,
  resendText,
  resources,
}) => {
  const setEditMode = (value) => {
    if (isPrimary) {
      onEdit('isEditing', value);
    } else {
      onEdit(`${groupName}___list`, index, 'isEditing', value);
    }
  };
  const getShowContinueAsButton = () => {
    const firstNameKey = Object.keys(data).find((clientId) => clientId.includes('_firstName'));
    return (
      (data.appRequest_signers_status &&
        data.appRequest_signers_status !== 'COMPLETED' &&
        !isEditing &&
        !data.needSignerEdit &&
        !!data[firstNameKey]) ||
      false
    );
  };

  const getContinueAsLabel = () => {
    const firstNameKey = Object.keys(data).find((clientId) => clientId.includes('_firstName'));
    return resources.continueAsSignerLabel?.replace('%s', data[firstNameKey]);
  };

  const handleOnRemove = () => {
    onRemove(index);
  };

  const getFullName = () => {
    const firstName = Object.keys(data).find((clientId) => clientId.includes('_firstName'));
    const lastName = Object.keys(data).find((clientId) => clientId.includes('_lastName'));

    return `${data[firstName] || ''} ${data[lastName] || ''}`;
  };

  const roleField = fields.find(
    (field) =>
      field.clientId.includes('_signerApplicantRole') || field.clientId.includes('_businessRole'),
  );

  const getRoleTypes = (clientId) =>
    listGroup?.rules
      ?.filter((rule) => rule.applicantRoles.includes(data[clientId]))
      ?.map((role) => resources[role.applicantRoleType])
      ?.join(', ');

  const getValue = (field) => {
    if (field.lookupValues) {
      return field.lookupValues[data[field?.clientId]];
    }
    if (field.fieldType === 'MASKED_SECRET_STRING') {
      let maskedValue = '';
      const value = data[field?.clientId];
      const { format } = field;

      if (value) {
        let k = 0;
        for (let i = 0; i < format.length; i += 1) {
          if (!value[k]) {
            break;
          } else if (format[i].match(/[*\d\w]/)) {
            maskedValue += format[i] === '*' ? '*' : value[k];

            k += 1;
          } else {
            maskedValue += format[i];
          }
        }
      }
      return maskedValue;
    }
    return data[field?.clientId];
  };

  const getFieldRow = (field) => {
    if (
      (isPrimary &&
        (field?.clientId?.includes('_firstName') || field?.clientId?.includes('_lastName'))) ||
      (!isEditing &&
        (field?.clientId?.includes('_firstName') || field?.clientId?.includes('_lastName'))) ||
      (!isEditing && field.clientId === roleField?.clientId)
    ) {
      return null;
    }

    if (isPrimary) {
      if (field?.clientId === roleField?.clientId) {
        return (
          <li key={field.clientId} className={classnames('list-item-field', classes.listItemField)}>
            <Field fieldName={field.clientId} groupName={groupName} />
            <Button
              className={classnames('btn btn-mobile btn-secondary btn-done', classes.doneButton)}
              variant="tertiary"
              onClick={() => {
                onDone(groupName, index);
              }}
            >
              Done
            </Button>
          </li>
        );
      }

      return (
        <li key={field?.clientId} className={classnames('list-item-row', classes.listItemRow)}>
          <span className={classnames('label', classes.label)}>{field?.label}</span>{' '}
          <span className={classnames('value', classes.value)}>
            {field.lookupValues ? field.lookupValues[data[field?.clientId]] : data[field?.clientId]}
          </span>
        </li>
      );
    }

    if (field?.clientId?.includes('_signers_status')) {
      return !isEditing ? (
        <li key={field.clientId} className={classnames('list-item-row', classes.listItemRow)}>
          <span className={classnames('label', classes.label)}>{field.label}</span>{' '}
          <span className={classnames('value', classes.value)}>
            {field.lookupValues ? field.lookupValues[data[field?.clientId]] : data[field?.clientId]}
            {data.needResendEmail && resendText && (
              <span
                tabIndex="0"
                role="button"
                className={classnames('buttonLink btn-resend-email', classes.resendButton)}
                onClick={(e) => {
                  e.preventDefault();
                  onResendEmail(index);
                }}
              >
                {` - ${resendText}`}
              </span>
            )}
          </span>
        </li>
      ) : null;
    }

    return isEditing ? (
      <li key={field.clientId} className={classnames('list-item-field', classes.listItemField)}>
        <Field
          fieldName={`${field.clientId}:${index}`}
          groupName={groupName}
          listGroupName={`${groupName}___list`}
        />
      </li>
    ) : (
      data[field?.clientId] &&
        (field?.clientId === roleField?.clientId ? (
          <li key={field?.clientId} className={classnames('list-item-row', classes.listItemRow)}>
            <span className={classnames('label', classes.label)}>{field?.label}</span>{' '}
            <span className={classnames('value', classes.value)}>
              {getRoleTypes(field.clientId)}
            </span>
          </li>
        ) : (
          <li key={field.clientId} className={classnames('list-item-row', classes.listItemRow)}>
            <span className={classnames('label', classes.label)}>{field.label}</span>{' '}
            <span className={classnames('value', classes.value)}>{getValue(field)}</span>
          </li>
        ))
    );
  };
  // ('appRequest_primary_beneficiaries_socialSecurityNumber');

  return (
    <li
      className={classnames('list-item', classes.listItem, {
        [classes.editingListItem]: isEditing,
        editing: isEditing,
      })}
    >
      {(isPrimary || !isEditing) && (
        <span className={classnames('title', classes.title)}>{getFullName()}</span>
      )}
      <div className={classnames('toolbar', classes.toolbar)}>
        {isPrimary && data.needPrimaryEdit && !isEditing && (
          <span
            className={classnames('edit-role buttonLink', classes.edit)}
            role="button"
            tabIndex="0"
            onClick={() => setEditMode(true)}
          >
            {primaryResources.editPrimary}
          </span>
        )}
        {!isPrimary && (data.needSignerEdit === true || data.needSignerEdit === undefined) && (
          <>
            {!isEditing && (
              <span
                className={classnames('edit-role buttonLink', classes.edit)}
                role="button"
                tabIndex="0"
                onClick={() => setEditMode(true)}
              >
                Edit
              </span>
            )}
            {!hideRemove && (
              <span
                className={classnames('remove buttonLink', classes.remove)}
                role="button"
                tabIndex="0"
                onClick={handleOnRemove}
              >
                Remove{' '}
                <CloseXAshIcon
                  className={classnames('icon', classes.icon)}
                  height="8px"
                  width="8px"
                />
              </span>
            )}
          </>
        )}
      </div>
      <ol>
        {roleField && !isEditing && (
          <li className={classnames('roles', classes.roles)}>
            {roleField?.lookupValues
              ? roleField?.lookupValues?.[data[roleField?.clientId]]
              : data[roleField?.clientId]}
          </li>
        )}
        {fields.map(getFieldRow)}
      </ol>
      {onDone && isEditing && !isPrimary && (
        <Button
          className={classnames('btn btn-mobile btn-secondary btn-done', classes.doneButton)}
          variant="tertiary"
          onClick={() => {
            onDone(groupName, index);
          }}
          disabled={disableButton}
        >
          Done
        </Button>
      )}
      {getShowContinueAsButton() ? (
        <Button className="btn-continue-as" variant="tertiary" onClick={() => onContinue(index)}>
          {getContinueAsLabel()}
        </Button>
      ) : null}
    </li>
  );
};

ListGroup.propTypes = {
  businessUsersResources: PropTypes.shape({}),
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  data: PropTypes.shape({
    appRequest_signers_status: PropTypes.string,
    needPrimaryEdit: PropTypes.bool,
    needResendEmail: PropTypes.bool,
    needSignerEdit: PropTypes.bool,
  }),
  disableButton: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  groupName: PropTypes.string.isRequired,
  hideRemove: PropTypes.bool,
  index: PropTypes.number,
  isEditing: PropTypes.bool,
  isPrimary: PropTypes.bool,
  listGroup: PropTypes.shape({
    rules: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  onContinue: PropTypes.func.isRequired,
  onDone: PropTypes.func,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
  onResendEmail: PropTypes.func.isRequired,
  primaryResources: PropTypes.shape({
    editPrimary: PropTypes.string,
  }),
  resendText: PropTypes.string,
  resources: PropTypes.shape({
    continueAsSignerLabel: PropTypes.string,
  }),
};

ListGroup.defaultProps = {
  businessUsersResources: {},
  data: {},
  disableButton: false,
  fields: [],
  hideRemove: false,
  index: null,
  isEditing: false,
  isPrimary: false,
  listGroup: {},
  onDone: undefined,
  onEdit: undefined,
  onRemove: undefined,
  primaryResources: {},
  resendText: '',
  resources: {},
};

const mapStateToProps = ({ data, fields, groups }, { groupName, index }) => {
  const isPrimary = index === null;
  const listFields = (isPrimary
    ? groups[groupName]?.fields?.map((fieldName) => fields[fieldName])
    : groups[groupName]?.fields?.map((fieldName) => fields[`${fieldName}:${index}`])
  )?.filter((field) => field !== undefined);
  const listItemData = isPrimary ? data : data[`${groupName}___list`]?.[index];
  const isEditing = isPrimary ? data.isEditing : listItemData?.isEditing;
  const hideRemove = index === 0 && isEditing;

  let disableButton = false;

  if (!isPrimary && isEditing && listFields && listItemData) {
    const hasUserStartedEditing = listFields.some((field) => {
      if (field.hidden) return false;
      const fieldValue = listItemData[field.clientId.replace(`:${index}`, '')];
      return fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;
    });

    const hasIncompleteRequiredFields = listFields.some((field) => {
      if (!field.required || field.hidden) return false;
      const fieldValue = listItemData[field.clientId.replace(`:${index}`, '')];
      const isEmpty =
        fieldValue === '' ||
        fieldValue === null ||
        fieldValue === undefined ||
        (field.fieldType === 'BOOLEAN' && fieldValue === false);
      return isEmpty;
    });

    const hasFieldError = listFields.some((field) => field.error);

    disableButton = !hasUserStartedEditing || hasIncompleteRequiredFields || hasFieldError;
  }

  return {
    data: listItemData,
    disableButton,
    fields: listFields,
    hideRemove,
    isEditing,
    isPrimary,
    listGroup: isPrimary ? groups.BUSINESS_USERS?.listGroup : groups[groupName]?.listGroup,
    primaryResources: groups.PRIMARY_USER?.resources,
    resendText: groups[groupName]?.resources?.resendEmailLabel ?? '',
    resources: isPrimary ? groups.BUSINESS_USERS?.resources : groups[groupName]?.resources,
  };
};

const mapDispatchToProps = {
  onContinue: pageActions.continueUser,
  onResendEmail: pageActions.resendEmail,
};

const ListGroupStyled = withStyles(styles)(ListGroup);

export default connect(mapStateToProps, mapDispatchToProps)(ListGroupStyled);
